import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/i18n";

export default function LanguageSwitcher({ mobile = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === (i18n.language || "en").split("-")[0]) ||
    SUPPORTED_LANGUAGES[0];

  const choose = (code) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem("mirrorloops-lang", code);
    } catch (_) {}
    setOpen(false);
  };

  if (mobile) {
    return (
      <div data-testid="language-switcher-mobile" className="border border-white/10 rounded">
        <div className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono flex items-center gap-2">
          <Globe size={11} /> {t("nav.language", "Language")}
        </div>
        <div className="grid grid-cols-2">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => choose(l.code)}
              data-testid={`language-mobile-${l.code}`}
              className={`px-3 py-2 text-sm text-left border-t border-white/5 ${
                current.code === l.code
                  ? "text-white bg-white/[0.04]"
                  : "text-white/60 hover:text-white"
              }`}
            >
              <span className="font-mono text-[11px] opacity-60 mr-2">{l.code.toUpperCase()}</span>
              {l.native}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher-btn"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/60 hover:text-white border border-white/10 hover:border-white/25 transition rounded-full font-mono uppercase tracking-[0.14em]"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Globe size={12} /> {current.code.toUpperCase()}
      </button>
      {open && (
        <div
          role="menu"
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-2 w-44 bg-[#141110] border border-white/10 backdrop-blur-xl shadow-2xl py-1 z-50"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => choose(l.code)}
              data-testid={`language-option-${l.code}`}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm transition ${
                current.code === l.code
                  ? "text-white bg-white/[0.04]"
                  : "text-white/70 hover:text-white hover:bg-white/[0.03]"
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="font-mono text-[10px] opacity-60 w-6">{l.code.toUpperCase()}</span>
                <span>{l.native}</span>
              </span>
              {current.code === l.code && <Check size={12} className="opacity-70" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
